/**
 * Press Grid - Web Component
 * Displays press items in a grid with a detail modal.
 * Clicking any image in the modal opens the theme gallery-viewer for zoom.
 */

class PressGrid extends HTMLElement {
  constructor() {
    super();
    this.items = [];
    this.currentIndex = 0;
    this.currentSlide = 0;
    this.modal = null;
    this.galleryModal = null;
    this.boundKeyHandler = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    try {
      this.items = JSON.parse(this.dataset.items || '[]');
    } catch (e) {
      console.error('[PressGrid] Failed to parse items:', e);
      this.items = [];
    }

    this.sortItemsByOrder();

    this.modal = this.querySelector('.press-grid__modal');

    // Clone gallery-viewer template for zoom
    const zoomTemplate = this.querySelector('.js-press-gallery-template');
    if (zoomTemplate) {
      this.galleryModal = zoomTemplate.content.firstElementChild.cloneNode(true);
    }

    // Card clicks
    this.querySelectorAll('.press-grid__card').forEach((card, index) => {
      card.addEventListener('click', () => this.openModal(index));
    });

    // Modal close
    const closeBtn = this.querySelector('.press-grid__modal-close');
    if (closeBtn) closeBtn.addEventListener('click', () => this.closeModal());

    // Backdrop close
    if (this.modal) {
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) this.closeModal();
      });
    }

    // Nav between press items
    const prevBtn = this.querySelector('.press-grid__modal-nav-btn--prev');
    const nextBtn = this.querySelector('.press-grid__modal-nav-btn--next');
    if (prevBtn) prevBtn.addEventListener('click', () => this.prevItem());
    if (nextBtn) nextBtn.addEventListener('click', () => this.nextItem());

    // Gallery slideshow arrows
    const galleryPrev = this.querySelector('.press-grid__gallery-arrow--prev');
    const galleryNext = this.querySelector('.press-grid__gallery-arrow--next');
    if (galleryPrev) galleryPrev.addEventListener('click', () => this.prevSlide());
    if (galleryNext) galleryNext.addEventListener('click', () => this.nextSlide());

    // Cover image click -> zoom
    const cover = this.querySelector('.press-grid__modal-cover');
    if (cover) cover.addEventListener('click', () => this.openZoom(0));

    // Fix gallery-viewer close button (clicks on child spans don't match .js-close-modal)
    if (this.galleryModal) {
      const closeBtn = this.galleryModal.querySelector('.gallery-viewer__close-btn');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.galleryModal.close());
      }
    }
  }

  sortItemsByOrder() {
    const indexed = this.items.map((item, i) => ({ item, originalIndex: i }));
    indexed.sort((a, b) => {
      const orderA = parseInt(a.item.order, 10) || 999;
      const orderB = parseInt(b.item.order, 10) || 999;
      return orderA - orderB;
    });

    this.items = indexed.map(entry => entry.item);

    const container = this.querySelector('.press-grid__items');
    if (!container) return;

    const cards = Array.from(container.querySelectorAll('.press-grid__card'));
    const sortedCards = indexed.map(entry => cards[entry.originalIndex]);

    sortedCards.forEach((card, i) => {
      card.setAttribute('data-index', i);
      container.appendChild(card);
    });
  }

  /* ---- Modal open/close ---- */

  openModal(index) {
    if (!this.modal || !this.items[index]) return;

    this.currentIndex = index;
    this.currentSlide = 0;
    this.renderModalContent();

    this.modal.style.display = '';
    this.modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.boundKeyHandler);

    if (typeof trapFocus === 'function') trapFocus(this.modal);
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.boundKeyHandler);
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
  }

  handleKeydown(e) {
    if (e.key === 'Escape') this.closeModal();
  }

  /* ---- Navigate between press items ---- */

  prevItem() {
    this.currentIndex = (this.currentIndex - 1 + this.items.length) % this.items.length;
    this.currentSlide = 0;
    this.renderModalContent();
  }

  nextItem() {
    this.currentIndex = (this.currentIndex + 1) % this.items.length;
    this.currentSlide = 0;
    this.renderModalContent();
  }

  /* ---- Render modal for current press item ---- */

  renderModalContent() {
    const item = this.items[this.currentIndex];
    if (!item) return;

    // Info
    const titleEl = this.querySelector('.press-grid__modal-title');
    if (titleEl) titleEl.textContent = item.title || '';

    const dateEl = this.querySelector('.press-grid__modal-date');
    if (dateEl) {
      if (item.date) {
        dateEl.textContent = item.date;
        dateEl.style.display = '';
      } else {
        dateEl.style.display = 'none';
      }
    }

    const descEl = this.querySelector('.press-grid__modal-description');
    if (descEl) descEl.innerHTML = item.description || '';

    // Cover image
    const coverContainer = this.querySelector('.press-grid__modal-cover');
    const coverImg = coverContainer ? coverContainer.querySelector('img') : null;
    if (coverImg) {
      if (item.cover_image) {
        coverImg.src = item.cover_image;
        coverImg.alt = item.title || '';
        coverContainer.style.display = '';
      } else {
        coverContainer.style.display = 'none';
      }
    }

    // Gallery slideshow (only gallery_images, not cover)
    const galleryImages = (item.gallery_images || []).filter(Boolean);
    const gallerySection = this.querySelector('.press-grid__modal-gallery');
    const track = this.querySelector('.press-grid__gallery-track');
    const dotsContainer = this.querySelector('.press-grid__gallery-dots');

    if (gallerySection && track && dotsContainer) {
      if (galleryImages.length > 0) {
        gallerySection.removeAttribute('hidden');

        // Build slides
        track.innerHTML = galleryImages.map((url, i) => `
          <div class="press-grid__gallery-slide" data-slide-index="${i}">
            <img src="${url}" alt="${item.title || ''} - Image ${i + 1}">
          </div>
        `).join('');

        // Bind click on each slide to open zoom
        track.querySelectorAll('.press-grid__gallery-slide').forEach((slide) => {
          slide.addEventListener('click', () => {
            const slideIndex = parseInt(slide.dataset.slideIndex, 10);
            // In zoom, cover is index 0, gallery images start at 1
            this.openZoom(slideIndex + 1);
          });
        });

        // Build dots
        dotsContainer.innerHTML = galleryImages.map((_, i) => `
          <button type="button" class="press-grid__gallery-dot ${i === 0 ? 'is-active' : ''}" data-dot-index="${i}" aria-label="Image ${i + 1}"></button>
        `).join('');

        dotsContainer.querySelectorAll('.press-grid__gallery-dot').forEach((dot) => {
          dot.addEventListener('click', () => {
            this.goToSlide(parseInt(dot.dataset.dotIndex, 10));
          });
        });

        // Reset position
        this.currentSlide = 0;
        track.style.transform = 'translateX(0)';
        this.updateGalleryArrows(galleryImages.length);
      } else {
        gallerySection.setAttribute('hidden', '');
      }
    }
  }

  /* ---- Gallery slideshow navigation ---- */

  prevSlide() {
    if (this.currentSlide > 0) this.goToSlide(this.currentSlide - 1);
  }

  nextSlide() {
    const item = this.items[this.currentIndex];
    const total = (item.gallery_images || []).filter(Boolean).length;
    if (this.currentSlide < total - 1) this.goToSlide(this.currentSlide + 1);
  }

  goToSlide(index) {
    const item = this.items[this.currentIndex];
    const total = (item.gallery_images || []).filter(Boolean).length;
    this.currentSlide = Math.max(0, Math.min(index, total - 1));

    const track = this.querySelector('.press-grid__gallery-track');
    if (track) track.style.transform = `translateX(-${this.currentSlide * 100}%)`;

    // Update dots
    this.querySelectorAll('.press-grid__gallery-dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.currentSlide);
    });

    this.updateGalleryArrows(total);
  }

  updateGalleryArrows(total) {
    const prevArrow = this.querySelector('.press-grid__gallery-arrow--prev');
    const nextArrow = this.querySelector('.press-grid__gallery-arrow--next');
    if (prevArrow) prevArrow.disabled = this.currentSlide <= 0;
    if (nextArrow) nextArrow.disabled = this.currentSlide >= total - 1;
  }

  /* ---- Open gallery-viewer for zoom ---- */

  openZoom(imageIndex) {
    const item = this.items[this.currentIndex];
    if (!item || !this.galleryModal) return;

    // All images: cover first, then gallery
    const allImages = [item.cover_image, ...(item.gallery_images || [])].filter(Boolean);
    if (allImages.length === 0) return;

    const safeIndex = Math.max(0, Math.min(imageIndex, allImages.length - 1));

    // Populate gallery-viewer thumbs
    const viewer = this.galleryModal.querySelector('gallery-viewer');
    const thumbsContainer = viewer.querySelector('.gallery-viewer__thumbs');
    thumbsContainer.innerHTML = allImages.map((url, i) => `
      <a class="gallery-viewer__thumb"
         href="#"
         data-zoom-url="${url}"
         data-media-id="press-${this.currentIndex}-${i}"
         data-media-type="image">
        <img class="gallery-viewer__thumb-img" src="${url}" alt="${item.title || ''} - Image ${i + 1}">
      </a>
    `).join('');

    // Update counter
    const totalMedia = allImages.length;
    viewer.setAttribute('data-total-media', totalMedia);
    viewer.classList.toggle('gallery-viewer--single-image', totalMedia === 1);
    const counterTotal = viewer.querySelector('.gallery-viewer__counter-total');
    if (counterTotal) counterTotal.textContent = totalMedia;

    // Append to body if needed
    if (!this.galleryModal.parentElement) {
      document.body.appendChild(this.galleryModal);
    }

    // Reset initialised so connectedCallback re-runs bindings
    viewer.initialised = false;

    // Open and init on the clicked image
    this.galleryModal.open(this.modal);
    viewer.init(allImages[safeIndex]);
    viewer.focus();
  }
}

customElements.define('press-grid', PressGrid);
